import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  StocktakeLocationLine,
  StocktakeLocationLiveResult,
  StocktakeLocationStatusQuantities,
} from 'src/interfaces/stocktake-location';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class StocktakeService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;
  totalNumberOfOrders: number = 0;
  totalNumberOfBatches: number = 0;
  totalNumberOfBatchItems: number = 0;

  constructor(
    private _auth: AuthService,
    private _http: HttpClient,
    private _utilsService: UtilsService,
    private _alertService: AlertService
  ) {
    this._auth.generateRequestHeaders().then((headers) => {
      this.headers = headers;
    });
  }

  async getCount(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktake/count/`, payload, {
          headers: this.headers,
        })
        .toPromise();

      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }

  async addBarcode(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktakebarcode/scan/`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.openSnackBar(data);

      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }

  async checkStocktake(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktake/check/`, payload, {
          headers: this.headers,
        })
        .toPromise();
      return message;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }
  async updateStocktake(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktake/update/`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.openSnackBar(data);
      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }
  async diffReportStocktake(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktake/report/difference`, payload, {
          headers: this.headers,
        })
        .toPromise();
      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }
  async scannedReportStocktake(payload: any): Promise<any | null> {
    try {
      const { message, data } = await this._http
        .post<any>(`${this.baseUrl}/stocktake/report/scanned`, payload, {
          headers: this.headers,
        })
        .toPromise();
      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }

  /**
   * Location live view — aggregated lines by medication, strength, and packaging.
   * Backend: GET `/stocktake/location/live`.
   * Query params: `page`, `limit`, `search`, `order_by`, `order_direction`,
   * optional `include_on_hold`, `include_not_yet_issued`, `expand_sibling_pack_lines`.
   * Expected body: `data: { results: [...], total: number }` (also accepts `items` / `rows` / `lines`).
   */
  async getLocationLiveView(options: {
    page?: number;
    limit?: number;
    search?: string;
    orderBy?: string;
    orderDirection?: string;
    includeOnHold?: boolean;
    includeNotYetIssued?: boolean;
    /** When true (and usually without `search`), ask the API to load all pack lines for medicines in the page. */
    expandSiblingPackLines?: boolean;
  }): Promise<StocktakeLocationLiveResult | null> {
    const page = options.page ?? 1;
    const limit = options.limit ?? 20;
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    const search = (options.search ?? '').trim();
    if (search) {
      params = params.set('search', search);
    }
    if (options.orderBy) {
      params = params.set('order_by', options.orderBy);
    }
    if (options.orderDirection) {
      params = params.set('order_direction', options.orderDirection);
    }
    if (options.includeOnHold === false) {
      params = params.set('include_on_hold', '0');
    }
    if (options.includeNotYetIssued === false) {
      params = params.set('include_not_yet_issued', '0');
    }
    if (options.expandSiblingPackLines === true) {
      params = params.set('expand_sibling_pack_lines', 'true');
    }
    try {
      const res = await this._http
        .get<any>(`${this.baseUrl}/stocktake/location/live`, {
          headers: this.headers,
          params,
        })
        .toPromise();
      const raw = res?.data !== undefined ? res.data : res;
      const list = StocktakeService.extractLineArray(raw);
      const items = list.map((row: any, index: number) =>
        StocktakeService.normalizeLocationLine(row, index)
      );
      const total = StocktakeService.extractTotalCount(raw, items.length);
      return { items, total };
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  private static extractTotalCount(raw: any, itemsLength: number): number {
    if (raw == null) {
      return itemsLength;
    }
    if (Array.isArray(raw)) {
      return raw.length;
    }
    if (typeof raw === 'object') {
      const t =
        raw.total ??
        raw.count ??
        raw.total_count ??
        raw.totalCount ??
        raw.meta?.total;
      const n = Number(t);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return itemsLength;
  }

  private static extractLineArray(raw: any): any[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw && typeof raw === 'object') {
      const nested =
        raw.results ??
        raw.items ??
        raw.rows ??
        raw.lines ??
        raw.data;
      if (Array.isArray(nested)) {
        return nested;
      }
    }
    return [];
  }

  private static normalizeLocationLine(row: any, index: number): StocktakeLocationLine {
    const med = row.medicine ?? row.medicine_name ?? row.medicineName ?? {};
    const medicineName =
      (typeof med === 'object' && med !== null ? (med as { name?: string }).name : null) ??
      (typeof med === 'string' ? med : null) ??
      row.medicine_name ??
      row.medicineName ??
      '';
    const mdp =
      row.medicine_dosage_package_type ??
      row.medicineDosagePackageType ??
      {};
    const dosageType =
      row.dosage_type ??
      row.dosageType ??
      mdp.dosage_type ??
      mdp.dosageType ??
      {};
    const pkgType =
      mdp.package_type ??
      mdp.packageType ??
      row.package_type ??
      row.packageType ??
      {};

    const strengthLabel =
      row.strength_label ??
      row.strengthLabel ??
      StocktakeLocationLine_strengthFromRow(row, dosageType, mdp);

    const packagingLabel =
      row.packaging_label ??
      row.packagingLabel ??
      mdp.label_name ??
      mdp.labelName ??
      StocktakeLocationLine_packagingLabel(mdp, pkgType);

    const containedRaw =
      mdp.contained_quantity ??
      mdp.containedQuantity ??
      row.contained_quantity ??
      row.containedQuantity;
    const packSizeLabel = formatContainedQuantity(containedRaw);
    const packSizeTop = row.pack_size ?? row.packSize;
    const packSizeNum = StocktakeService.parsePackSize(packSizeTop, containedRaw);

    const itemLabel =
      row.item_label ??
      row.itemLabel ??
      row.item ??
      dosageType.additional_information ??
      dosageType.name ??
      '';

    const q =
      row.quantities ??
      row.quantity_by_status ??
      row.status_quantities ??
      row ??
      {};
    const quantities: StocktakeLocationStatusQuantities = {
      onHold: StocktakeService.num(
        q.on_hold ?? q.onHold ?? row.on_hold ?? row.onHold
      ),
      toBeSentOut: StocktakeService.num(
        q.to_be_sent_out ?? q.toBeSentOut ?? row.to_be_sent_out ?? row.toBeSentOut
      ),
      captured: StocktakeService.num(q.captured ?? row.captured),
      reserved: StocktakeService.num(q.reserved ?? row.reserved),
      notYetIssued: StocktakeService.num(
        q.not_yet_issued ??
          q.notYetIssued ??
          row.not_yet_issued ??
          row.notYetIssued
      ),
    };

    const onHandApi = row.on_hand ?? row.onHand;
    const onHand =
      onHandApi !== undefined && onHandApi !== null && onHandApi !== ''
        ? StocktakeService.num(onHandApi)
        : quantities.captured + quantities.reserved;

    const availableApi = row.available;
    const available =
      availableApi !== undefined && availableApi !== null && availableApi !== ''
        ? StocktakeService.num(availableApi)
        : onHand - quantities.reserved;

    const backOrder = StocktakeService.num(
      row.back_order ?? row.backOrder
    );

    const lowStock = StocktakeService.boolish(row.low_stock ?? row.lowStock);

    const legacyTotalRaw = row.total;
    const legacyTotalSum =
      legacyTotalRaw !== undefined &&
      legacyTotalRaw !== null &&
      legacyTotalRaw !== ''
        ? StocktakeService.num(legacyTotalRaw)
        : undefined;

    const rowKey =
      String(
        row.row_key ??
          row.rowKey ??
          row.medicine_dosage_package_type_id ??
          row.medicineDosagePackageTypeId ??
          `${medicineName}-${packagingLabel}-${index}`
      );

    const line: StocktakeLocationLine = {
      rowKey,
      medicineName: String(medicineName),
      itemLabel: String(itemLabel),
      strengthLabel: String(strengthLabel),
      packagingLabel: String(packagingLabel),
      packSize: packSizeNum,
      packSizeLabel,
      onHand,
      available,
      backOrder,
      lowStock,
      quantities,
    };
    if (legacyTotalSum !== undefined) {
      line.legacyTotalSum = legacyTotalSum;
    }
    return line;
  }

  private static num(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  /** True for API truthy flags: true, 1, "1", "true", "yes" (case-insensitive). */
  private static boolish(v: unknown): boolean {
    if (v === true || v === 1) {
      return true;
    }
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes';
    }
    return false;
  }

  /** Prefer top-level `pack_size`, else nested contained quantity. */
  private static parsePackSize(
    packSizeTop: unknown,
    containedRaw: unknown
  ): number | null {
    if (packSizeTop !== undefined && packSizeTop !== null && packSizeTop !== '') {
      const n = Number(packSizeTop);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    if (containedRaw !== undefined && containedRaw !== null && containedRaw !== '') {
      const n = Number(containedRaw);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return null;
  }
}

function StocktakeLocationLine_strengthFromRow(
  row: any,
  dosageType: any,
  mdp: any
): string {
  const unit = dosageType?.dosage_unit?.unit ?? dosageType?.dosageUnit?.unit ?? '';
  const strength =
    row.dosage_strength ??
    row.dosageStrength ??
    dosageType?.dosage_strength ??
    dosageType?.dosageStrength ??
    mdp?.dosage_strength;
  if (strength !== undefined && strength !== null && strength !== '') {
    return unit ? `${strength} ${unit}`.trim() : String(strength);
  }
  return dosageType?.additional_information ?? dosageType?.name ?? '';
}

function StocktakeLocationLine_packagingLabel(mdp: any, pkgType: any): string {
  const contained = mdp?.contained_quantity ?? mdp?.containedQuantity;
  const typeName = pkgType?.type ?? pkgType?.name ?? '';
  if (contained != null && typeName) {
    return `${typeName} (${contained})`;
  }
  if (contained != null) {
    return `Pack of ${contained}`;
  }
  return typeName || '';
}

/** Pack size column: numeric string, or empty when unknown. */
function formatContainedQuantity(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
}
